import { useState, useEffect, useRef } from 'react';
import { 
  FaCheckCircle, 
  FaGraduationCap, 
  FaUsers, 
  FaGlobe, 
  FaAward,
  FaBookOpen,
  FaHeart,
  FaLightbulb,
  FaFlag,
  FaStar,
  FaChevronRight
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  const sectionRefs = useRef({});
  const [visibleSections, setVisibleSections] = useState(new Set());

  useEffect(() => {
    const observers = [];
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections((prev) => new Set([...prev, entry.target.id]));
        }
      });
    };

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) {
        const observer = new IntersectionObserver(observerCallback, observerOptions);
        observer.observe(ref);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const values = [
    {
      icon: FaHeart,
      title: 'Passion for Education',
      description: 'We are deeply committed to spreading Islamic knowledge and helping students connect with the Quran.',
      color: 'var(--islamic-green)'
    },
    {
      icon: FaLightbulb,
      title: 'Innovation',
      description: 'We leverage modern technology to make Quran learning accessible, convenient, and effective for everyone.',
      color: 'var(--golden)'
    },
    {
      icon: FaAward,
      title: 'Excellence',
      description: 'We maintain the highest standards in teaching quality, ensuring authentic and accurate Quranic education.',
      color: 'var(--islamic-green-dark)'
    },
    {
      icon: FaStar,
      title: 'Integrity',
      description: 'We operate with honesty, transparency, and respect for Islamic values in all our interactions.',
      color: 'var(--golden)'
    }
  ];

  const features = [
    'Qualified and experienced native Arab teachers',
    'One-on-one personalized attention',
    'Flexible scheduling to fit your lifestyle',
    'Interactive online classes with modern tools',
    'Affordable pricing with various payment options',
    'Progress tracking and regular assessments',
    'Free trial classes available',
    'Certificate upon course completion'
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <div className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="container">
          <div className="about-hero-content">
            <span className="about-badge">Our Story</span>
            <h1 className="about-hero-title">About ALNoor Tutors</h1>
            <p className="about-hero-subtitle">
              Empowering Muslims worldwide to learn and understand the Holy Quran through quality online education
            </p>
          </div>
        </div>
      </div>

      {/* Welcome Section */}
      <section 
        className={`about-welcome-section ${visibleSections.has('welcome') ? 'visible' : ''}`}
        id="welcome"
        ref={(el) => (sectionRefs.current['welcome'] = el)}
      >
        <div className="container">
          <div className="welcome-content">
            <div className="welcome-text">
              <span className="section-badge">Welcome</span>
              <h2>Welcome to ALNoor Tutors</h2>
              <p className="welcome-intro">
                ALNoor Tutors is a leading online platform dedicated to providing high-quality 
                Quran education to students worldwide. Our mission is to make Quranic knowledge accessible 
                to everyone, regardless of their location or time constraints.
              </p>
              <p>
                With years of experience and a team of qualified teachers, we have helped thousands 
                of students from around the globe to learn, understand, and connect with the Holy Quran. 
                We believe that every Muslim deserves access to authentic Islamic education, and we're 
                here to make that possible.
              </p>
            </div>
            <div className="welcome-image">
              <div className="image-placeholder">
                <FaBookOpen />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section 
        className={`about-vision-mission ${visibleSections.has('vision') ? 'visible' : ''}`}
        id="vision"
        ref={(el) => (sectionRefs.current['vision'] = el)}
      >
        <div className="container">
          <div className="vision-mission-grid">
            <div className={`vision-card ${visibleSections.has('vision') ? 'visible' : ''}`}>
              <div className="card-icon-wrapper">
                <FaLightbulb />
              </div>
              <h3>Our Vision</h3>
              <p>
                To become the world's most trusted and comprehensive online Quran learning platform, 
                empowering millions of Muslims to connect with the Holy Quran through quality education 
                and authentic Islamic teachings.
              </p>
            </div>
            <div className={`mission-card ${visibleSections.has('vision') ? 'visible' : ''}`} style={{ animationDelay: '0.2s' }}>
              <div className="card-icon-wrapper">
                <FaFlag />
              </div>
              <h3>Our Mission</h3>
              <p>
                We strive to provide authentic, professional, and convenient Quran education through 
                experienced teachers and modern technology, making it easy for anyone to learn and 
                understand the Quran from the comfort of their home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section 
        className={`about-values-section ${visibleSections.has('values') ? 'visible' : ''}`}
        id="values"
        ref={(el) => (sectionRefs.current['values'] = el)}
      >
        <div className="container">
          <div className="values-header">
            <span className="section-badge">Our Values</span>
            <h2>What We Stand For</h2>
            <p>These core values guide everything we do at ALNoor Tutors</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className={`value-card ${visibleSections.has('values') ? 'visible' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="value-icon-wrapper" style={{ backgroundColor: `${value.color}15`, color: value.color }}>
                    <Icon />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        className={`about-features-section ${visibleSections.has('features') ? 'visible' : ''}`}
        id="features"
        ref={(el) => (sectionRefs.current['features'] = el)}
      >
        <div className="container">
          <div className="features-content">
            <div className="features-header">
              <span className="section-badge">Why Choose Us</span>
              <h2>What Makes Us Different</h2>
              <p>Discover the advantages of learning with ALNoor Tutors</p>
            </div>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`feature-item ${visibleSections.has('features') ? 'visible' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <FaCheckCircle className="feature-check" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <div className="cta-content">
            <div className="cta-icon-wrapper">
              <FaGraduationCap />
            </div>
            <h2>Ready to Start Your Quran Learning Journey?</h2>
            <p>Join thousands of students learning Quran online with expert tutors. Start your journey with a free trial today!</p>
            <div className="cta-buttons">
              <Link to="/free-trial" className="cta-btn-primary">
                <span>Start Free Trial</span>
                <FaChevronRight className="btn-arrow" />
              </Link>
              <Link to="/courses" className="cta-btn-secondary">
                View Our Courses
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
